import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

/**
 * Stable filter for every `ContentPage`-style entity in the catalog.
 *
 * Decision recap: we settled on **plain `Component` with
 * `spec.type: content-page`** instead of a custom kind. Pros:
 *   - No need to extend `catalog.rules` in `app-config.yaml` beyond
 *     the `Component` allow that already exists.
 *   - No custom processor needed to validate the kind.
 *   - The entities still show up in `/catalog` (handy for ownership,
 *     orphan detection, and quick admin search by name), but the
 *     `spec.type` filter keeps them distinct from real software
 *     Components.
 *
 * Naming convention: `metadata.name` IS the URL slug. We chose this
 * because Backstage entity names already MUST be kebab-case and
 * URL-safe (catalog enforces `^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$`), so
 * the entity name doubles as the slug with zero extra validation.
 *
 * Cache key: `[catalogApi]` only. The hook does NOT re-fetch on slug
 * changes — `useAsync` re-runs only when dependencies change. We
 * always load the FULL list once and look up the slug client-side
 * (cheap: a couple dozen entities at most). This means the sidebar
 * and every page render share the same in-memory list, so navigating
 * between content pages is instant after the first load.
 */
const CONTENT_PAGE_FILTER = {
  kind: 'Component',
  'spec.type': 'content-page',
};

type ContentPagesState = {
  entities: Entity[];
  loading: boolean;
  error: Error | undefined;
};

/**
 * Lists every content-page entity in the catalog. Used both by the
 * sidebar (to render one nav entry per page) and by
 * `DynamicContentPage` (to find the entity that matches the current
 * `:slug`).
 */
export const useContentPages = (): ContentPagesState => {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: CONTENT_PAGE_FILTER,
    });
    return items;
  }, [catalogApi]);

  return {
    entities: value ?? [],
    loading,
    error,
  };
};

/**
 * Look up a single content-page entity by its slug
 * (`metadata.name`). Returns the matching entity or `undefined` if no
 * page is registered under that slug — the page renderer turns the
 * latter into a 404-style error panel.
 */
export const useContentPage = (
  slug: string | undefined,
): { entity: Entity | undefined; loading: boolean; error: Error | undefined } => {
  const { entities, loading, error } = useContentPages();
  const entity = slug
    ? entities.find(e => e.metadata.name === slug)
    : undefined;
  return { entity, loading, error };
};
